import { isValidPhoneNumber } from 'react-phone-number-input'

import { differenceInSeconds, isBefore } from 'date-fns'
import * as Yup from 'yup'

import { INFO_CONSTANTS, MIN_SCHEDULE_TIME_IN_MINUTES } from 'features/Home/constants/infoConstants'
import type { AppointmentDate } from 'features/Home/interfaces/IInfoSchedule'

const checkIsValidAppointment = (appointment: AppointmentDate) => {
  const isValidAppt = appointment?.length !== 2
  if (isValidAppt) {
    return true
  }

  if (!appointment[0] && appointment[1]) {
    throw new Yup.ValidationError(INFO_CONSTANTS.PLEASE_SELECT_START_TIME, null, 'appointment')
  }

  if (appointment[0] && !appointment[1]) {
    throw new Yup.ValidationError(INFO_CONSTANTS.PLEASE_SELECT_END_TIME, null, 'appointment')
  }

  const [startTime, endTime] = appointment
  const start = new Date(startTime)
  const end = new Date(endTime)

  if (!isBefore(start, end)) {
    throw new Yup.ValidationError(INFO_CONSTANTS.EARLY_END_TIME_ERROR_MESSAGE, null, 'appointment')
  }

  const timeGap = Math.round(differenceInSeconds(end, start) / 60)

  if (timeGap < MIN_SCHEDULE_TIME_IN_MINUTES) {
    throw new Yup.ValidationError(
      INFO_CONSTANTS.EARLY_END_TIME_FIVE_MINUTES_GAP_ERROR,
      null,
      'appointment',
    )
  }

  return true
}

export const NEW_PATIENT_SCHEMA = Yup.object().shape({
  first_name: Yup.string()
    .required('Please enter the first name')
    .min(1, 'First name must contain at least 1 character'),
  last_name: Yup.string()
    .required('Please enter the last name')
    .min(1, 'Last name must contain at least 1 character'),
  phone: Yup.string()
    .nullable()
    .required('Please enter the mobile phone')
    .min(1, 'Please enter a valid mobile phone.')
    .test('is-valid-phone', 'Please enter a valid mobile phone', function (value) {
      return value && isValidPhoneNumber(value)
    }),
  language: Yup.string().required('Please select the language'),
  room: Yup.string().required('Please select the room'),
  appointment: Yup.array()
    .required('Please select the appointment time')
    .test({
      name: 'is-valid-appointment',
      message: 'Invalid appointment time',
      test: (value) => checkIsValidAppointment(value as AppointmentDate),
    }),
})

export type ManageAppointmentFormType = Yup.InferType<typeof NEW_PATIENT_SCHEMA>

export const EHR_APPOINTMENT_SCHEMA = Yup.object().shape({
  first_name: NEW_PATIENT_SCHEMA.fields.first_name,
  last_name: NEW_PATIENT_SCHEMA.fields.last_name,
  phone: Yup.string().optional().nullable(),
  language: Yup.number().required('Please select the language'),
  room: NEW_PATIENT_SCHEMA.fields.room,
  duration: Yup.number().required('Please select the duration'),
  date: Yup.string().required('Please select the date'),
  time: Yup.date().required('Please select the time'),
  id: Yup.number().nullable(),
  work_phone: Yup.string().optional().nullable(),
  home_phone: Yup.string().optional().nullable(),
  punctuality_type_code: Yup.number().nullable(),
  punctuality_minutes: Yup.number().nullable(),
  patient_punctuality: Yup.object()
    .shape({
      code: Yup.number().nullable(),
      minutes: Yup.number().nullable(),
    })
    .nullable(),
})

export type EHRAppointmentFormType = Yup.InferType<typeof EHR_APPOINTMENT_SCHEMA>
